import { css, CSSResult } from 'lit'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import { csColorAction, csColorBorder } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csBorder } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { csShadow } from '@cloudsuite/mosaic-shared/styles/shadows.style.js'
import { SCREENS } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'


export const partsStyle: CSSResult = css`
  :host {
    display: contents;
  }
`

// Component-local CSS variable map
export const CSCBaseProductTile = {
  borderColor: css`var(--csc-base-product-tile-border-color, ${csColorBorder.light})`,
  borderColorHover: css`var(--csc-base-product-tile-border-color-hover, ${csColorAction.linkPrimaryHover})`,
  borderWidth: css`var(--csc-base-product-tile-border-width, 0 0 ${csBorder.width} 0)`,
  borderRadius: css`var(--csc-base-product-tile-border-radius, ${csBorder.radiusNone})`,
  boxShadowHover: css`var(--csc-base-product-tile-box-shadow-hover, ${csShadow.default})`,

  // "header" here means the image area (slot="header" holds the image, plus
  // any image-above/image-below widgets in list/inline mode) — renamed from
  // --csc-base-product-tile-header-background-color, which named it after its
  // slot instead of what it actually is. The old name is kept as a fallback
  // for existing theme overrides (e.g. pda-mosaic). Also fixes a real bug in
  // the old declaration: its own fallback, --cse-card-background-color, was
  // never wrapped in var(), so it was invalid CSS and silently dropped the
  // whole background declaration for every theme that didn't set either name.
  imageBackgroundColor: css`var(--csc-base-product-tile-image-background-color, var(--csc-base-product-tile-header-background-color, var(--cse-card-background-color)))`,

  wideImageWidth: css`var(--csc-base-product-tile-wide-image-width, 200px)`,
  inlineImageWidth: css`var(--csc-base-product-tile-inline-image-width, 100px)`,
  imageBorderRadius: css`var(--csc-base-product-tile-image-border-radius, ${csBorder.radiusNone})`,

  listBorderRadius: css`var(--csc-base-product-tile-list-border-radius, 0)`,
  listBorderWidth: css`var(--csc-base-product-tile-list-border-width, 0 0 ${csSpacing.spacing00} 0)`,
  listBoxShadowHover: css`var(--csc-base-product-tile-list-box-shadow-hover, none)`,
}

export const style: CSSResult = css`
  :host([card-variant="wide"]),
  :host([card-variant="inline"]) {
    --cse-card-border-radius: ${CSCBaseProductTile.listBorderRadius};
    --cse-card-border-width: ${CSCBaseProductTile.listBorderWidth};
    --cse-card-box-shadow-hover: ${CSCBaseProductTile.listBoxShadowHover};
  }

  /* -body-padding-* and -footer-padding-* (wide and inline) are superseded by tile-config's own
     productInfo/orderActions area padding — same mechanism, and same reasoning, as
     .product-tile-header's image padding above: whenever an author configures area padding,
     base-product-tile.ts zeroes these two custom properties inline on <cse-card> itself, which
     always wins over whatever's inherited here regardless of what these resolve to. So they're
     no longer first-class CSCBaseProductTile entries — kept inline, hardcoded to spacing05 by
     default, purely as a legacy fallback for bijoumoderne, which still sets all 8 (confirmed via
     grep across every frontend theme repo) to real, non-default (and non-square, shorthand)
     values. */
  :host([card-variant="wide"]) {
    /* --cse-card-gap and --cse-card-padding-y/x retired from the active map: the product
       direction is no styling around/between areas (image/productInfo/orderActions) as a
       whole — a whole-card outer padding is the same shape of thing as a between-area gap,
       only padding within an area (AreaPaddingCfg) and gap within a zone are kept
       first-class. bijoumoderne genuinely sets --wide-gap to a non-default value though
       (grep across every frontend theme repo), so kept inline as a legacy fallback rather
       than dropped outright; -wide-padding-y/x are unused by every theme repo but get the
       same treatment for consistency. */
    --cse-card-gap: var(--csc-base-product-tile-wide-gap, 0);
    --cse-card-padding-y: var(--csc-base-product-tile-wide-padding-y, 0);
    --cse-card-padding-x: var(--csc-base-product-tile-wide-padding-x, 0);
    --cse-card-body-padding-y: var(--csc-base-product-tile-wide-body-padding-y, ${csSpacing.spacing05});
    --cse-card-body-padding-x: var(--csc-base-product-tile-wide-body-padding-x, ${csSpacing.spacing05});
    --cse-card-footer-padding-y: var(--csc-base-product-tile-wide-footer-padding-y, ${csSpacing.spacing05});
    --cse-card-footer-padding-x: var(--csc-base-product-tile-wide-footer-padding-x, ${csSpacing.spacing05});
  }

  :host([card-variant="inline"]) {
    /* Same reasoning and treatment as card-variant="wide" above — here -inline-padding-y/x
       are the ones with a real theme dependency (fyron sets both to a non-default value)
       rather than -inline-gap. */
    --cse-card-gap: var(--csc-base-product-tile-inline-gap, 0);
    --cse-card-padding-y: var(--csc-base-product-tile-inline-padding-y, 0);
    --cse-card-padding-x: var(--csc-base-product-tile-inline-padding-x, 0);
    --cse-card-body-padding-y: var(--csc-base-product-tile-inline-body-padding-y, ${csSpacing.spacing05});
    --cse-card-body-padding-x: var(--csc-base-product-tile-inline-body-padding-x, ${csSpacing.spacing05});
    --cse-card-footer-padding-y: var(--csc-base-product-tile-inline-footer-padding-y, ${csSpacing.spacing05});
    --cse-card-footer-padding-x: var(--csc-base-product-tile-inline-footer-padding-x, ${csSpacing.spacing05});
  }

  :host {
    /* Confirmed unused by every frontend theme repo — no longer a named
       CSCBaseProductTile entry, kept inline as a legacy fallback only. */
    margin-bottom: var(--csc-base-product-tile-margin-bottom, 0);
    margin-left: var(--csc-base-product-tile-margin-left, 0);
    margin-right: var(--csc-base-product-tile-margin-right, 0);
    margin-top: var(--csc-base-product-tile-margin-top, 0);
    /* Not themeable — the default (grid) variant's header/body/footer gap is
       fixed at 0, deliberately, not a --csc-base-product-tile-gap CSS
       variable. Wide/inline have their own separate -wide-gap/-inline-gap,
       both legacy-fallback-only now (see the note on card-variant="wide"
       below) — the product direction is no gap between areas at all
       (image/productInfo/orderActions), only padding within one and gaps
       within a zone; -wide-gap/-inline-gap predate that and aren't a
       pattern to extend to grid/list or reintroduce elsewhere. */
    --cse-card-gap: 0;

    /* Bridge base-product-tile border vars to the underlying cse-card. */
    --csc-base-product-tile-card-border-radius: ${CSCBaseProductTile.borderRadius};
    --csc-base-product-tile-card-border-width: ${CSCBaseProductTile.borderWidth};
    --csc-base-product-tile-card-border-color: ${CSCBaseProductTile.borderColor};
    --csc-base-product-tile-card-border-color-hover: ${CSCBaseProductTile.borderColorHover};
    --csc-base-product-tile-card-box-shadow-hover: ${CSCBaseProductTile.boxShadowHover};

    --cse-card-border-radius: var(--csc-base-product-tile-card-border-radius, ${csBorder.radiusMinimal});
    --cse-card-border-width: var(--csc-base-product-tile-card-border-width, 0 0 ${csBorder.width} 0);
    --cse-card-border-color: var(--csc-base-product-tile-card-border-color, ${csColorBorder.light});
    --cse-card-border-color-hover: var(
      --csc-base-product-tile-card-border-color-hover,
      ${csColorBorder.light}
    );
    --cse-card-box-shadow-hover: var(
      --csc-base-product-tile-card-box-shadow-hover,
      ${csShadow.default}
    );
    
    /* Ensure the tile takes full height in grid */
    height: 100%;
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 100%;

    --cse-card-height: 100%;
    --cse-card-body-flex-grow: 1;

    --csc-base-product-tile-hover-opacity: 0;
    --csc-base-product-tile-hover-visibility: hidden;
  }

  :host(:hover),
  :host(:focus-within) {
    --csc-base-product-tile-hover-opacity: 1;
    --csc-base-product-tile-hover-visibility: visible;
  }

  /* Force card to take full height and use flexbox */
  cse-card {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  .product-tile-body {
    min-width: 0;
    max-width: 100%;
  }

  .product-tile-header {
    /* Image area padding is now authored via tile-config's own area padding
       (ProductImageViewConfig's paddingTop/Right/Bottom/Left) — applied by
       base-product-tile.ts's cardSlotPaddingVars into cse-card's own
       --cse-card-header-padding-y/x (inline on <cse-card>, not here), zeroing this rule's
       own padding-top/bottom inline on .product-tile-header itself whenever it's configured
       — see renderFromSimpleLayout. Routed through the cse-card variable rather than a literal
       padding-* on this element (unlike .product-tile-body/-footer, which have never had a
       stylesheet default of their own to begin with) specifically because card-variant="wide"/
       "inline" gives this element a hard-coded width + flex: 0 0 <width> for the fixed-width
       side-by-side image column below — a literal padding-left/right on that same fixed-basis
       box doesn't produce the expected "space added around a fixed image size" result; the
       cse-card slot wrapper this now targets sits outside that fixed-width box entirely, so it
       doesn't hit the conflict. No longer a first-class, actively-initialized CSCBaseProductTile
       entry — the old theme var below is kept inline, hardcoded to 0 by default, purely as a
       legacy fallback for existing overrides (bijoumoderne, pda-mosaic both still set the old
       --csc-base-product-tile-header-padding-y name to a real, non-default value — confirmed via
       grep across every frontend theme repo). */
    padding-top: var(--csc-base-product-tile-image-padding-y, var(--csc-base-product-tile-header-padding-y, 0));
    padding-bottom: var(--csc-base-product-tile-image-padding-y, var(--csc-base-product-tile-header-padding-y, 0));
    background: ${CSCBaseProductTile.imageBackgroundColor};

    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: ${csSpacing.spacing05};

    @media(min-width: ${SCREENS.medium}) {
      align-items: center;
    }
  }

  .product-tile-header csc-product-image {
    --cse-image-max-width: 100%;
    display: block;
    overflow: hidden;
    border-radius: ${CSCBaseProductTile.imageBorderRadius};
  }

  .image-wrapper {
    position: relative;
    flex: 1;
    width: 100%;
  }

  :host([card-variant="wide"]) .product-tile-header {
    width: ${CSCBaseProductTile.wideImageWidth};
    flex: 0 0 ${CSCBaseProductTile.wideImageWidth};
    align-self: stretch;
  }

  :host([card-variant="inline"]) .product-tile-header {
    width: ${CSCBaseProductTile.inlineImageWidth};
    flex: 0 0 ${CSCBaseProductTile.inlineImageWidth};
    align-self: stretch;
  }

  :host([card-variant="wide"]) .image-wrapper,
  :host([card-variant="inline"]) .image-wrapper {
    overflow: hidden;
  }

  :host([card-variant="wide"]) .product-tile-header csc-product-image,
  :host([card-variant="inline"]) .product-tile-header csc-product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* flex-direction/flex-wrap/align-items: was theme-configurable via
     --csc-base-product-tile-info-flex-direction/-flex-wrap/-align-items — confirmed unused by
     every frontend theme repo, so hardcoded to their only-ever-used defaults. Not yet exposed
     in the configurator (explicitly deferred; this is a section-level control shared by
     productInfo and orderActions alike — both call renderDynamicSection — not a per-zone one,
     so it needs its own design before it gets a config surface). Revisit if a real need for
     this to be configurable resurfaces.
     gap IS configurable now, unlike the three above — this is the row gap (between stacked
     rows), authored via DynamicSectionCfg.rowGap and applied as a literal inline style by
     renderDynamicSection (base-product-tile.ts), same mechanism as area padding. This CSS
     value is only the unconfigured default. */
  .content {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: ${csSpacing.spacing05};
    min-width: 0;
    max-width: 100%;
  }

  .product-tile-footer {
    min-width: 0;
    max-width: 100%;
  }

  /* List view: productInfo/orderActions fill the full tile height (cse-card already stretches
     its own .body/.footer wrappers in its wide/inline row layout), so DynamicSectionCfg.
     verticalAlign — applied inline on .content by renderDynamicSection — has room to align the
     rows. Unconfigured, .content keeps its rows at the top, so this changes nothing visually.
     .footer is made a flex container (--cse-card-footer-display) so .product-tile-footer can
     stretch to it — height: 100% isn't usable here, WebKit resolves it against .footer's
     border-box instead of its content box. In inline .footer is content-sized (stacked under
     .body, which already grows), so it's a no-op there. */
  :host([card-variant="wide"]),
  :host([card-variant="inline"]) {
    --cse-card-footer-display: flex;
  }

  :host([card-variant="wide"]) .product-tile-body,
  :host([card-variant="inline"]) .product-tile-body,
  :host([card-variant="wide"]) .product-tile-footer,
  :host([card-variant="inline"]) .product-tile-footer {
    display: flex;
    flex-direction: column;
  }

  :host([card-variant="wide"]) .product-tile-body,
  :host([card-variant="inline"]) .product-tile-body,
  :host([card-variant="wide"]) .product-tile-footer,
  :host([card-variant="inline"]) .product-tile-footer {
    flex: 1 1 auto;
  }

  :host([card-variant="wide"]) .content,
  :host([card-variant="inline"]) .content {
    flex: 1 1 auto;
  }

  .product-tile-footer .position-area.left {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* New generic overlay placement API (8 slots around image) */
  .image-area {
    position: absolute;
    display: flex;
    align-items: center;
    gap: ${csSpacing.spacing05};
    pointer-events: none; /* let widgets opt-in if they need clicks */
  }
  .image-area > * { pointer-events: auto; }

  .image-area.top-left { top: 0; left: 0; margin: ${csSpacing.spacing05}; }
  .image-area.top-center { top: 0; left: 50%; transform: translateX(-50%); margin-top: ${csSpacing.spacing05}; }
  .image-area.top-right { top: 0; right: 0; margin: ${csSpacing.spacing05}; }

  .image-area.middle-left { top: 50%; left: 0; transform: translateY(-50%); margin-left: ${csSpacing.spacing05}; }
  .image-area.middle-right { top: 50%; right: 0; transform: translateY(-50%); margin-right: ${csSpacing.spacing05}; }

  .image-area.bottom-left { bottom: 0; left: 0; margin: ${csSpacing.spacing05}; }
  .image-area.bottom-center { bottom: 0; left: 50%; transform: translateX(-50%); margin-bottom: ${csSpacing.spacing05}; }
  .image-area.bottom-right { bottom: 0; right: 0; margin: ${csSpacing.spacing05}; }

  /* Opt-in per overlay place (ImageOverlayHoverConfig.hideUntilHoverPlaces) — same
     ancestor-driven hover vars :host(:hover)/:host(:focus-within) set above, previously
     consumed individually by csc-compare-checkbox/csc-product-tile-add-to-order-template's own
     now-removed hideUntilHover property. */
  .image-area.hover-reveal {
    opacity: var(--csc-base-product-tile-hover-opacity, 1);
    visibility: var(--csc-base-product-tile-hover-visibility, visible);
    transition: opacity 0.15s ease-in-out;
  }

  /* Keeps multi-line widget text (e.g. a wrapping title) aligned the same way
     as the corner itself, instead of defaulting to the browser's left
     alignment — some widgets (cse-link/cse-text) expose their own CSS
     variable for this instead of inheriting text-align. */
  .image-area.top-left, .image-area.middle-left, .image-area.bottom-left {
    text-align: left;
    --cse-link-text-align: left;
    --cse-text-text-align: left;
  }
  .image-area.top-center, .image-area.bottom-center {
    text-align: center;
    --cse-link-text-align: center;
    --cse-text-text-align: center;
  }
  .image-area.top-right, .image-area.middle-right, .image-area.bottom-right {
    text-align: right;
    --cse-link-text-align: right;
    --cse-text-text-align: right;
  }

  /* In-flow image zones for list (wide) and mobile (inline) variants — above/below are a real
     (if always-singular) dynamic zone each now, not a flattened flex-through wrapper: the
     outer .image-above/.image-below carries the zone's own padding (paddingStyleFor), the
     inner .zone (renderZone, same helper productInfo/orderActions zones use) carries its own
     direction/justify-content/align-items/gap. Only one .product-tile-header-level gap now
     applies between the zone and the image, not one per widget inside it — that's now the
     zone's own gap instead (see .image-above .zone/.image-below .zone below). Only ever
     rendered in wide/inline variants (see renderImageFlowZone's cardVariant check), but scoped
     here anyway to match how every other image-area rule in this stylesheet is scoped.
     width: 100% (not just max-width) matters here: .product-tile-header's own align-items is
     flex-start/center (never stretch — see the .product-tile-header rule above), so without an
     explicit width its cross-axis children (this and .image-wrapper) would each shrink-to-fit
     their own content instead of spanning the header's full width. Without it, the inner
     .zone's own justifyContent/alignItems (e.g. flex-start) only ever aligns within that
     shrunk box — which, centered or left-aligned by the *header's* own align-items, visually
     reads as centered/off regardless of what the zone itself is configured to do. Mirrors the
     canvas's own .canvas-image-flow-zone (tile-configurator-canvas.style.ts), which already
     has this. */
  :host([card-variant="wide"]) .image-above,
  :host([card-variant="wide"]) .image-below,
  :host([card-variant="inline"]) .image-above,
  :host([card-variant="inline"]) .image-below {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  /* Dynamic zone layout for productInfo/orderActions: each row is either a single
     full-width zone or 2 zones side by side, and each zone is its own flex container
     whose direction/justify-content/align-items/gap come from the tile-config JSON
     (applied inline per zone, since they're genuinely dynamic per-author values —
     see CSCBaseProductTile.renderZone). This file only supplies the structural
     part: how a row's 1 or 2 zones share its width. gap here is the column gap
     (between the up to 2 zones within a split row) — authored via
     DynamicSectionCfg.columnGap and applied inline by renderDynamicSection, same
     mechanism as .content's row gap above; this CSS value is only the unconfigured
     default, not a hardcoded ceiling. */
  .zone-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${csSpacing.spacing05};
    min-width: 0;
    max-width: 100%;
  }

  .zone-row.full-width > .zone {
    flex: 1 1 100%;
    min-width: 0;
  }

  .zone-row.split > .zone {
    flex: 1 1 0%;
    min-width: 0;
  }

  .zone {
    display: flex;
    max-width: 100%;
  }

  /* Zones default to this gap between their own widgets — a zone's inline gap
     (see CSCBaseProductTile.renderZone) overrides this once the author
     configures one explicitly. Same default for productInfo, orderActions, and
     the above/below image-flow zones. */
  .product-tile-body .zone,
  .product-tile-footer .zone,
  .image-above .zone,
  .image-below .zone {
    gap: ${csSpacing.spacing05};
  }

  .widget-instance {
    display: contents;
  }

  .skeleton-loading {
    display: block;
    width: 100%;
  }
` 
